/**
 * Dashboard Hooks
 * Manages admin dashboard data
 */

import { useApi } from './useApi';
import { adminAPI } from '@/services/api';
import type { DashboardResponse, BonusConfigResponse, TurnoverReportResponse, LimitsResponse } from '@/types';

// =============================================================================
// Dashboard Data Hook
// =============================================================================

export interface UseDashboardOptions {
  fetchOnMount?: boolean;
  onSuccess?: (data: DashboardResponse) => void;
  onError?: (error: string) => void;
}

/**
 * Hook for fetching admin dashboard data
 *
 * NOTE: Backend returns stats directly, NOT wrapped in { stats: ... }
 *
 * @example
 * const { dashboard, isLoading, refetch } = useDashboard({ fetchOnMount: true });
 * // dashboard.users.total, dashboard.finance.profit, etc.
 */
export function useDashboard(options: UseDashboardOptions = {}) {
  const { fetchOnMount = false, onSuccess, onError } = options;

  const api = useApi<DashboardResponse>(
    () => adminAPI.getDashboard(),
    { fetchOnMount, onSuccess, onError }
  );

  return {
    ...api,
    dashboard: api.data,
    // Direct access to sections
    users: api.data?.users ?? null,
    finance: api.data?.finance ?? null,
    deposits: api.data?.deposits ?? null,
    system: api.data?.system ?? null,
    refetch: api.execute,
  };
}

// =============================================================================
// Bonus Configuration Hook
// =============================================================================

export interface UseBonusConfigOptions {
  fetchOnMount?: boolean;
  onSuccess?: (data: BonusConfigResponse) => void;
  onError?: (error: string) => void;
}

/**
 * Hook for managing bonus configuration
 *
 * @example
 * const { bonusConfig, updateBonus, isLoading } = useBonusConfig({ fetchOnMount: true });
 */
export function useBonusConfig(options: UseBonusConfigOptions = {}) {
  const { fetchOnMount = false, onSuccess, onError } = options;

  const api = useApi<BonusConfigResponse>(
    () => adminAPI.getBonusConfig(),
    { fetchOnMount, onSuccess, onError }
  );

  const updateDepositApi = useApi<{ message: string }, [{ percentage: number }]>(
    (data) => adminAPI.updateBonusPercentage(data)
  );

  const updateRefereeApi = useApi<{ message: string }, [{ percentage: number; refereeConversionPercent: number }]>(
    (data) => adminAPI.updateBonusPercentage(data)
  );

  return {
    ...api,
    bonusConfig: api.data,
    percentage: api.data?.percentage ?? 0,
    refereeConversionPercent: api.data?.refereeConversionPercent ?? 0,
    refetch: api.execute,
    // Separate update functions - NO ENTANGLEMENT
    updateDeposit: async (percentage: number) => {
      const currentReferee = api.data?.refereeConversionPercent ?? 0;
      await updateDepositApi.execute({ percentage, refereeConversionPercent: currentReferee } as any);
    },
    updateReferee: async (refereePercent: number) => {
      const currentDeposit = api.data?.percentage ?? 0;
      await updateRefereeApi.execute({ percentage: currentDeposit, refereeConversionPercent: refereePercent });
    },
    isUpdating: updateDepositApi.isLoading || updateRefereeApi.isLoading,
    updateError: updateDepositApi.error || updateRefereeApi.error,
  };
}

// =============================================================================
// Turnover Report Hook
// =============================================================================

export interface UseTurnoverReportOptions {
  onSuccess?: (data: TurnoverReportResponse) => void;
  onError?: (error: string) => void;
}

/**
 * Hook for fetching turnover reports
 *
 * NOTE: Backend returns report directly, NOT wrapped in { report: ... }
 *
 * @example
 * const { fetchReport, report, isLoading } = useTurnoverReport();
 * await fetchReport('monthly');
 * // report.period, report.deposits.count, report.net, etc.
 */
export function useTurnoverReport(options: UseTurnoverReportOptions = {}) {
  const api = useApi<TurnoverReportResponse, ['weekly' | 'monthly' | 'yearly']>(
    (period) => adminAPI.getTurnoverReport(period),
    options
  );

  return {
    ...api,
    report: api.data,
    fetchReport: api.execute,
  };
}

// =============================================================================
// Limits Configuration Hook
// =============================================================================

export interface UseLimitsOptions {
  fetchOnMount?: boolean;
  onSuccess?: (data: LimitsResponse) => void;
  onError?: (error: string) => void;
}

/**
 * Hook for managing payment method limits
 *
 * @example
 * const { limits, updateDepositLimit, updateWithdrawalLimit } = useLimits({ fetchOnMount: true });
 */
export function useLimits(options: UseLimitsOptions = {}) {
  const { fetchOnMount = false, onSuccess, onError } = options;

  const api = useApi<LimitsResponse>(
    () => adminAPI.getLimits(),
    { fetchOnMount, onSuccess, onError }
  );

  const updateDepositApi = useApi<unknown, [string, { minAmount?: number; maxAmount?: number }]>(
    (method, data) => adminAPI.updateDepositMethodLimits(method, data)
  );

  const updateWithdrawalApi = useApi<unknown, [string, { minAmount?: number; maxAmount?: number }]>(
    (method, data) => adminAPI.updateWithdrawalMethodLimits(method, data)
  );

  return {
    ...api,
    limits: api.data,
    depositLimits: api.data?.deposit ?? [],
    withdrawalLimits: api.data?.withdrawal ?? [],
    refetch: api.execute,

    // Update deposit limits
    updateDepositLimit: updateDepositApi.execute,
    isUpdatingDeposit: updateDepositApi.isLoading,
    depositUpdateError: updateDepositApi.error,

    // Update withdrawal limits
    updateWithdrawalLimit: updateWithdrawalApi.execute,
    isUpdatingWithdrawal: updateWithdrawalApi.isLoading,
    withdrawalUpdateError: updateWithdrawalApi.error,
  };
}
